import { useState } from 'react';
import { apiFetch, ApiError } from '../lib/api';

// #2369 — the FR-level structured editor: the safe, narrow PRD-edit
// affordance that pairs with #2368's rejection of raw notes overwrites.
// Parses FR-NN [confidence: X]: text lines client-side (display only —
// mirrors prdJsonSerializer.ts's own frPattern, but no structural decision
// depends on this parse; the server re-derives everything from the
// persisted markdown) and edits ONE FR at a time via
// PATCH /artefacts/:artefactId/fr/:frId.

interface PrdFr {
  id: string;
  confidence: number;
  text: string;
}

interface PrdFrEditorProps {
  artefactId: string;
  markdown: string;
  baseVersionId: string;
  onSaved: () => void;
}

function parseFrs(markdown: string): PrdFr[] {
  const pattern = /(?:^[-*]\s*)?(?:##?\s*)?(FR-\d+)\s*\[confidence:\s*([\d.]+)\]:?\s*(.*)$/gim;
  const results: PrdFr[] = [];
  let match: RegExpExecArray | null;
  while ((match = pattern.exec(markdown)) !== null) {
    const confidence = parseFloat(match[2]);
    if (Number.isNaN(confidence)) continue;
    results.push({ id: match[1].toUpperCase(), confidence, text: match[3].trim() });
  }
  return results;
}

export default function PrdFrEditor({ artefactId, markdown, baseVersionId, onSaved }: PrdFrEditorProps) {
  const frs = parseFrs(markdown);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draftText, setDraftText] = useState('');
  const [draftConfidence, setDraftConfidence] = useState(0.7);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [conflict, setConflict] = useState(false);

  if (frs.length === 0) return null;

  const startEdit = (fr: PrdFr) => {
    setEditingId(fr.id);
    setDraftText(fr.text);
    setDraftConfidence(fr.confidence);
    setError('');
    setConflict(false);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setError('');
    setConflict(false);
  };

  // R1 review (#2369) — Reload must clear the conflict banner and exit edit
  // mode itself, not just trigger the parent refetch. PrdFrEditor is never
  // remounted across that refetch (same position in the tree, no key change
  // tied to baseVersionId), so `conflict`/`editingId` are local state that
  // would otherwise survive fresh props indefinitely — a sticky banner that
  // outlives the very reload it prompted, and a stale draft left open ready
  // to be resubmitted against content the user hasn't looked at yet.
  const handleReload = () => {
    setConflict(false);
    setEditingId(null);
    setError('');
    onSaved();
  };

  const save = async (frId: string) => {
    setSaving(true);
    setError('');
    setConflict(false);
    try {
      await apiFetch(`/artefacts/${artefactId}/fr/${frId}`, {
        method: 'PATCH',
        body: JSON.stringify({ baseVersionId, text: draftText, confidence: draftConfidence }),
      });
      setEditingId(null);
      onSaved();
    } catch (err) {
      // The server rejects a write built on a version that is no longer
      // latest (a concurrent chat/refinement edit landed) — surface the
      // reload prompt rather than a generic error, and never retry the
      // write with the same stale baseVersionId.
      if (err instanceof ApiError && err.status === 409) {
        setConflict(true);
      } else {
        setError(err instanceof Error ? err.message : 'Failed to save');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="prd-fr-editor" style={{ marginBottom: 16, background: 'var(--surface)', borderRadius: 'var(--radius-sm)', padding: 16, border: '1px solid var(--border)' }}>
      <h3 style={{ fontFamily: 'var(--font-body)', fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 12 }}>
        Functional Requirements
      </h3>
      {conflict && (
        <div data-testid="fr-editor-conflict" style={{ marginBottom: 12, padding: '10px 12px', background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: 'var(--radius-sm)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
          <span style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: '#f59e0b' }}>The PRD changed since you opened this editor — your edit was NOT saved.</span>
          <button type="button" data-testid="fr-editor-reload" onClick={handleReload} style={{ fontFamily: 'var(--font-body)', fontSize: 12, fontWeight: 600, color: 'var(--primary)', background: 'none', border: 'none', cursor: 'pointer', flexShrink: 0 }}>
            Reload
          </button>
        </div>
      )}
      {frs.map((fr) => (
        <div key={fr.id} data-testid={`fr-row-${fr.id}`} style={{ padding: '10px 0', borderBottom: '1px solid var(--border)' }}>
          {editingId === fr.id ? (
            <div>
              <textarea
                data-testid="fr-edit-text"
                value={draftText}
                onChange={(e) => setDraftText(e.target.value)}
                rows={2}
                style={{ width: '100%', fontFamily: 'var(--font-body)', fontSize: 13, padding: 8, borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)', marginBottom: 8, resize: 'vertical' }}
              />
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <label htmlFor={`fr-confidence-${fr.id}`} style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-secondary)' }}>
                  Confidence:
                </label>
                <input
                  id={`fr-confidence-${fr.id}`}
                  data-testid="fr-edit-confidence"
                  type="number"
                  min={0}
                  max={1}
                  step={0.05}
                  value={draftConfidence}
                  onChange={(e) => setDraftConfidence(Number(e.target.value))}
                  style={{ width: 64, fontFamily: 'var(--font-mono)', fontSize: 12, padding: '2px 6px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)' }}
                />
              </div>
              {error && <div style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: '#ef4444', marginBottom: 8 }}>{error}</div>}
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  type="button"
                  data-testid="fr-edit-save"
                  disabled={saving || draftText.trim().length === 0}
                  onClick={() => save(fr.id)}
                  style={{ fontFamily: 'var(--font-body)', fontSize: 12, fontWeight: 600, padding: '4px 12px', borderRadius: 'var(--radius-sm)', background: 'var(--primary)', color: 'white', border: 'none', cursor: saving ? 'default' : 'pointer', opacity: saving || draftText.trim().length === 0 ? 0.6 : 1 }}
                >
                  {saving ? 'Saving…' : 'Save'}
                </button>
                <button
                  type="button"
                  data-testid="fr-edit-cancel"
                  onClick={cancelEdit}
                  disabled={saving}
                  style={{ fontFamily: 'var(--font-body)', fontSize: 12, fontWeight: 600, padding: '4px 12px', borderRadius: 'var(--radius-sm)', background: 'transparent', color: 'var(--text-secondary)', border: '1px solid var(--border)', cursor: 'pointer' }}
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
              <div>
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' }}>{fr.id}</span>{' '}
                <span style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text)' }}>{fr.text}</span>
              </div>
              <button
                type="button"
                data-testid={`fr-edit-button-${fr.id}`}
                onClick={() => startEdit(fr)}
                style={{ fontFamily: 'var(--font-body)', fontSize: 12, fontWeight: 600, color: 'var(--primary)', background: 'none', border: 'none', cursor: 'pointer', flexShrink: 0 }}
              >
                Edit
              </button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
